import React, { useEffect, useRef, useState } from 'react';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import './ImageGallery.css';

export type GalleryImage = {
  src: string;
  alt?: string;
  title?: string;
};

type ImageGalleryProps = {
  images: GalleryImage[];
  /** Which image to use as the thumbnail. Defaults to 0 (first) */
  thumbnailIndex?: number;
  /** Optional aria-label for the thumbnail button */
  thumbnailAriaLabel?: string;
};

const clampIndex = (i: number, len: number) => {
  if (len === 0) return 0;
  return (i + len) % len; // wrap-around
};

function ImageGallery({
  images,
  thumbnailIndex = 0,
  thumbnailAriaLabel = 'Open image gallery',
}: ImageGalleryProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [current, setCurrent] = useState<number>(thumbnailIndex);
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);

  const open = () => {
    setCurrent(clampIndex(thumbnailIndex, images.length));
    setIsOpen(true);
  };

  const close = () => {
    setIsOpen(false);
  };

  const next = () => setCurrent((c) => clampIndex(c + 1, images.length));
  const prev = () => setCurrent((c) => clampIndex(c - 1, images.length));

  // Keyboard controls when lightbox is open
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!isOpen) return;

      if (e.key === 'Escape') {
        e.preventDefault();
        close();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        next();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        prev();
      }
    };

    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [isOpen]); // always return a cleanup function → satisfies consistent-return

  // Move focus to close button when modal opens (accessibility)
  useEffect(() => {
    if (isOpen && closeBtnRef.current) {
      closeBtnRef.current.focus();
    }
  }, [isOpen]);

  if (!images || images.length === 0) {
    return null;
  }

  const thumb = images[clampIndex(thumbnailIndex, images.length)];
  const currentImage = images[clampIndex(current, images.length)];

  return (
    <div className="ig-root">
      {/* Thumbnail */}
      <button
        type="button"
        className="ig-thumb"
        onClick={open}
        aria-label={thumbnailAriaLabel}
      >
        <img
          className="ig-thumb-img"
          src={thumb.src}
          alt={thumb.alt ?? thumb.title ?? 'Gallery thumbnail'}
          loading="lazy"
        />
        <div className="ig-thumb-title">
          {thumb.title ?? 'Gallery'}
        </div>
      </button>

      {/* Lightbox */}
      {isOpen && (
        <div className="ig-modal" role="dialog" aria-modal="true" aria-label="Image gallery">
          {/* Make backdrop a real interactive element */}
          <button
            type="button"
            className="ig-backdrop"
            aria-label="Close gallery"
            onClick={close}
            onKeyDown={(e) => {
              if (e.key === ' ' || e.key === 'Enter') {
                e.preventDefault();
                close();
              }
            }}
            // If the backdrop covers the entire screen, it shouldn't be in the tab order:
            tabIndex={-1}
          />

          <div className="ig-modal-content" role="document">
            <figure className="ig-figure">
              <img
                className="ig-image"
                src={currentImage.src}
                alt={currentImage.alt ?? currentImage.title ?? `Image ${current + 1}`}
              />
              {(currentImage.title || currentImage.alt) && (
                <figcaption className="ig-caption">
                  {currentImage.title ?? currentImage.alt}
                </figcaption>
              )}
            </figure>

            <NavigateBeforeIcon className="ig-btn ig-prev" fontSize="large" onClick={prev} />
            <NavigateNextIcon className="ig-btn ig-next" fontSize="large" onClick={next} />

            <button
              className="ig-close"
              type="button"
              onClick={close}
              aria-label="Close gallery"
              ref={closeBtnRef}
            >
              ✕
            </button>

            <div className="ig-counter" aria-live="polite">
              {clampIndex(current, images.length) + 1}
              /
              {images.length}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ImageGallery;
